import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { acceptInvite } from '../services/shareService';

const STASH_KEY = 'nlg_pending_invite';

/**
 * Remember that this visitor arrived through someone's share link.
 *
 * sessionStorage on purpose: it survives an OAuth round trip (a full page
 * navigation away and back) and dies with the tab, which is exactly the
 * lifetime an in-progress invite should have.
 * @param {string} shareId
 */
export const stashInvite = (shareId) => {
  try {
    sessionStorage.setItem(STASH_KEY, shareId);
  } catch {
    // Private mode, or storage disabled. The connection is lost, not the
    // visit - they can still play, and still be added the next time.
  }
};

/**
 * Fires the invite acceptance once the visitor has a record of their own.
 *
 * **One mechanism, not a hook per action.** The connection could otherwise
 * need wiring into favoriting, scoring, signing up and clicking a game link
 * separately. Instead this watches the user id and fires when it goes from
 * absent to present - however that happened. A brand-new visitor who plays a
 * game and a returning user who was already signed in are the same code path.
 *
 * **Nothing happens on mere arrival** (D48). Without a record there is nothing
 * for a friend edge to point at, so walking share URLs creates nothing, which
 * is what makes guessable ids safe (D49).
 */
export const useInviteAcceptance = () => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const attempted = useRef(false);

  useEffect(() => {
    if (!userId || attempted.current) return;

    let pending = null;
    try {
      pending = sessionStorage.getItem(STASH_KEY);
    } catch {
      return;
    }
    if (!pending) return;

    // Guard within this mount as well as via the stash: the effect can re-run
    // before the await resolves, and the endpoint being idempotent is a
    // backstop, not a reason to call it twice.
    attempted.current = true;

    acceptInvite(pending)
      .catch((error) => {
        console.log('Error accepting invite:', error);
      })
      .finally(() => {
        try {
          sessionStorage.removeItem(STASH_KEY);
        } catch {
          // Nothing to do - a repeat call is a no-op server-side.
        }
      });
  }, [userId]);
};
